import { Box, Button, Group, Text } from '@mantine/core';

export interface ScaleOption {
  value: number;
  label: string;
}

interface ScaleQuestionProps {
  /** Question text shown above the scale. */
  label: string;
  /** Currently selected value, or null. */
  value: number | null;
  /** The 5 (or N) labeled anchors, low → high. */
  options: ScaleOption[];
  onPick: (value: number) => void;
  /** Prefix for the per-button data-testid (`${testid}-${value}`). */
  testid: string;
  disabled?: boolean;
  /** Show the required asterisk. Default true. */
  required?: boolean;
  /** Optional inline error shown below the row (e.g. required validation). */
  error?: string;
}

const LABEL_COLOR = '#374151';
const FIELD_BORDER = '#E5E7EB';

/**
 * MED-1055 / MED-1056 — reusable labeled ordinal scale: one row of full-width
 * buttons, each showing its anchor label, selected = medleyGreen. Shared by the
 * participant (LeaveFeedbackCard) and peer-leader (PeerLeaderFeedbackFormCard)
 * feedback forms so the 5-point scales — especially the shared "As a
 * Participant" set — render identically. Categorical single-selects stay as
 * radios; this is for true 1–N ordinal scales only.
 */
export function ScaleQuestion({
  label,
  value,
  options,
  onPick,
  testid,
  disabled = false,
  required = true,
  error,
}: ScaleQuestionProps) {
  return (
    <Box>
      <Text fz={13} fw={600} c={LABEL_COLOR} mb={6}>
        {label}
        {required && (
          <>
            {' '}
            <Text span c="dangerRed" aria-hidden>
              *
            </Text>
          </>
        )}
      </Text>
      <Group gap="xs" grow wrap="nowrap" align="stretch">
        {options.map(opt => {
          const selected = value === opt.value;
          return (
            <Button
              key={opt.value}
              variant={selected ? 'filled' : 'default'}
              color={selected ? 'medleyGreen' : undefined}
              disabled={disabled}
              onClick={() => onPick(opt.value)}
              data-testid={`${testid}-${opt.value}`}
              data-selected={selected ? 'true' : 'false'}
              styles={{
                root: {
                  height: 'auto',
                  minHeight: 56,
                  padding: '8px 6px',
                  borderRadius: 8,
                  ...(selected ? {} : { border: `1px solid ${FIELD_BORDER}` }),
                },
                label: {
                  whiteSpace: 'normal',
                  fontSize: 12,
                  lineHeight: 1.2,
                  textAlign: 'center',
                },
              }}
            >
              {opt.label}
            </Button>
          );
        })}
      </Group>
      {error && (
        <Text fz={12} c="dangerRed" mt={4}>
          {error}
        </Text>
      )}
    </Box>
  );
}

export default ScaleQuestion;
